import { apiClient } from '@/api/client';
import type {
  Facility,
  Worker,
  Alert,
  WorkerPath,
  Analytics,
  ZoneDwell,
  HeatmapData,
  TimeRange,
} from '@/types';

export interface LocalizationScorecardRow {
  worker_id: string;
  employee_id?: string | null;
  worker_name?: string | null;
  scale_unit: string;
  point_count: number;
  first_seen?: string | null;
  last_seen?: string | null;
  last_location_seen?: string | null;
  last_anchor_seen?: string | null;
  stale_seconds?: number | null;
  avg_confidence?: number | null;
  min_confidence?: number | null;
  avg_accuracy_meters?: number | null;
  max_accuracy_meters?: number | null;
  avg_step_units?: number | null;
  p95_step_units?: number | null;
  max_step_units?: number | null;
  jump_count: number;
  points_per_minute?: number | null;
  source_counts: Record<string, number>;
  anchor_window_count: number;
  avg_anchor_count?: number | null;
  min_anchor_count?: number | null;
  max_anchor_count?: number | null;
  avg_signal_quality?: number | null;
  avg_los_confidence?: number | null;
  needs_review: boolean;
}

export interface LocalizationScorecard {
  time_range: { start: string; end: string };
  jump_threshold_units: number;
  scale_unit: string;
  worker_count: number;
  workers_with_jumps: number;
  worst_jump_units: number;
  scorecard: LocalizationScorecardRow[];
}

type FacilityCreatePayload = Partial<Facility> & {
  contactEmail?: string;
  contactPhone?: string;
  contact_email?: string;
  contact_phone?: string;
};

// Map center and zoom were a Leaflet geo-map model nothing renders any more
// (F1-8); the client no longer injects placeholder coordinates.
function normalizeCreateFacilityPayload(facility: FacilityCreatePayload): FacilityCreatePayload {
  return {
    ...facility,
    contact_email: facility.contact_email ?? facility.contactEmail,
    contact_phone: facility.contact_phone ?? facility.contactPhone,
  };
}

// API service class
class ApiService {
  // Auth endpoints
  async login(email: string, password: string) {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  }

  async verifyMfaLogin(payload: { userId: string; tempToken: string; code: string }) {
    const response = await apiClient.post('/auth/mfa/login-verify', payload);
    return response.data;
  }

  async logout() {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  }

  async refreshToken() {
    // Refresh uses the httpOnly refresh cookie. No long-lived token is readable
    // or sent from browser JavaScript.
    const response = await apiClient.post('/auth/refresh', {});
    return response.data;
  }

  async getCurrentUser() {
    const response = await apiClient.get('/auth/me');
    return response.data;
  }

  /**
   * Exchange the httpOnly auth cookie for a short-lived (30s) WebSocket token.
   * The browser sends the cookie automatically; the server returns a JWT with
   * `purpose: 'ws'` that the Socket.IO handshake will accept. Keeps the
   * long-lived JWT out of WS URLs and proxy logs.
   */
  async getWsToken(): Promise<string> {
    const response = await apiClient.get('/auth/ws-token');
    return response.data?.data?.token;
  }

  async validateInvitationToken(token: string) {
    const response = await apiClient.post('/invitations/validate-token', { token });
    return response.data;
  }

  async acceptInvitation(payload: {
    token: string;
    firstName: string;
    lastName: string;
    password: string;
    phone?: string;
  }) {
    const response = await apiClient.post('/invitations/accept', payload);
    return response.data;
  }

  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email });
  }

  async resendVerification(email: string): Promise<void> {
    await apiClient.post('/auth/resend-verification', { email });
  }

  // Facilities endpoints
  async getFacilities(): Promise<Facility[]> {
    const response = await apiClient.get('/facilities');
    return response.data.data || response.data;
  }

  /** @deprecated Use getFacilities() instead */
  async getSites(): Promise<Facility[]> {
    return this.getFacilities();
  }

  async getFacility(facilityId: string): Promise<Facility> {
    const response = await apiClient.get(`/facilities/${facilityId}`);
    return response.data.data || response.data;
  }

  /** @deprecated Use getFacility() instead */
  async getSite(facilityId: string): Promise<Facility> {
    return this.getFacility(facilityId);
  }

  async createFacility(facility: FacilityCreatePayload): Promise<Facility> {
    const response = await apiClient.post('/facilities', normalizeCreateFacilityPayload(facility));
    return response.data;
  }

  /** @deprecated Use createFacility() instead */
  async createSite(facility: Partial<Facility>): Promise<Facility> {
    return this.createFacility(facility);
  }

  async createFacilityWithMap(formData: FormData): Promise<Facility> {
    const response = await apiClient.post('/facilities/with-map', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /** @deprecated Use createFacilityWithMap() instead */
  async createSiteWithMap(formData: FormData): Promise<Facility> {
    return this.createFacilityWithMap(formData);
  }

  async updateFacility(facilityId: string, updates: Partial<Facility>): Promise<Facility> {
    const response = await apiClient.patch(`/facilities/${facilityId}`, updates);
    return response.data.data || response.data;
  }

  /** @deprecated Use updateFacility() instead */
  async updateSite(facilityId: string, updates: Partial<Facility>): Promise<Facility> {
    return this.updateFacility(facilityId, updates);
  }

  async deleteFacility(facilityId: string): Promise<void> {
    await apiClient.delete(`/facilities/${facilityId}`);
  }

  /** @deprecated Use deleteFacility() instead */
  async deleteSite(facilityId: string): Promise<void> {
    return this.deleteFacility(facilityId);
  }

  // Map management endpoints
  async uploadFacilityMap(facilityId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('mapImage', file);
    const response = await apiClient.post(`/facilities/${facilityId}/map`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /** @deprecated Use uploadFacilityMap() instead */
  async uploadSiteMap(facilityId: string, file: File): Promise<any> {
    return this.uploadFacilityMap(facilityId, file);
  }

  async deleteFacilityMap(facilityId: string): Promise<void> {
    await apiClient.delete(`/facilities/${facilityId}/map`);
  }

  /** @deprecated Use deleteFacilityMap() instead */
  async deleteSiteMap(facilityId: string): Promise<void> {
    return this.deleteFacilityMap(facilityId);
  }

  async calibrateMap(facilityId: string, calibrationData: {
    imageWidth: number;
    imageHeight: number;
    referencePoints: Array<{
      imageCoord: { x: number; y: number };
      realCoord: { x: number; y: number };
    }>;
  }): Promise<any> {
    const response = await apiClient.post(`/facilities/${facilityId}/calibrate`, calibrationData);
    return response.data;
  }

  async convertCoordinates(facilityId: string, data: {
    coordinates: Array<{ x: number; y: number }>;
    direction: 'imageToReal' | 'realToImage';
  }): Promise<any> {
    const response = await apiClient.post(`/facilities/${facilityId}/convert-coordinates`, data);
    return response.data;
  }

  async updateFacilityCalibration(facilityId: string, calibrationData: {
    scaleValue: number;
    scaleUnit: string;
    pixelsPerUnit: number;
    calibrationLine?: {
      start: { x: number; y: number };
      end: { x: number; y: number };
      pixelDistance: number;
    };
  }): Promise<any> {
    const response = await apiClient.patch(`/facilities/${facilityId}/calibration`, calibrationData);
    return response.data;
  }

  /** @deprecated Use updateFacilityCalibration() instead */
  async updateSiteCalibration(facilityId: string, calibrationData: {
    scaleValue: number;
    scaleUnit: string;
    pixelsPerUnit: number;
    calibrationLine?: {
      start: { x: number; y: number };
      end: { x: number; y: number };
      pixelDistance: number;
    };
  }): Promise<any> {
    return this.updateFacilityCalibration(facilityId, calibrationData);
  }

  // Workers endpoints
  async getWorkers(facilityId: string): Promise<Worker[]> {
    const response = await apiClient.get(`/facilities/${facilityId}/workers`);
    return response.data.data || response.data;
  }

  async getWorkersWithPositions(facilityId: string, includeInactive: boolean = false): Promise<any[]> {
    const response = await apiClient.get(`/facilities/${facilityId}/workers-positions`, {
      params: { includeInactive: includeInactive.toString() }
    });
    return response.data.data || response.data;
  }

  async getWorker(facilityId: string, workerId: string): Promise<Worker> {
    const response = await apiClient.get(`/facilities/${facilityId}/workers/${workerId}`);
    return response.data.data || response.data;
  }

  async getWorkerLocations(facilityId: string, workerId: string, params?: {
    startTime?: string;
    endTime?: string;
    limit?: number;
  }) {
    const response = await apiClient.get(`/facilities/${facilityId}/workers/${workerId}/locations`, { params });
    return response.data;
  }

  async getWorkerPath(
    facilityId: string,
    workerId: string,
    date: string
  ): Promise<WorkerPath> {
    const response = await apiClient.get(
      `/facilities/${facilityId}/workers/${workerId}/path`,
      { params: { date } }
    );
    return response.data;
  }

  // Analytics endpoints
  async getFacilityAnalytics(
    facilityId: string,
    timeRange: TimeRange
  ): Promise<Analytics> {
    const response = await apiClient.get(
      `/facilities/${facilityId}/analytics`,
      {
        params: {
          start: timeRange.start.toISOString(),
          end: timeRange.end.toISOString(),
        }
      }
    );
    return response.data;
  }

  /** @deprecated Use getFacilityAnalytics() instead */
  async getSiteAnalytics(facilityId: string, timeRange: TimeRange): Promise<Analytics> {
    return this.getFacilityAnalytics(facilityId, timeRange);
  }

  async getZoneStatistics(
    facilityId: string,
    timeRange: TimeRange
  ): Promise<ZoneDwell[]> {
    const response = await apiClient.get(
      `/facilities/${facilityId}/analytics/zones`,
      {
        params: {
          start: timeRange.start.toISOString(),
          end: timeRange.end.toISOString(),
        }
      }
    );
    return response.data;
  }

  async getHeatmapData(
    facilityId: string,
    timeRange: TimeRange,
    workerIds?: string[]
  ): Promise<HeatmapData> {
    const response = await apiClient.post(
      `/facilities/${facilityId}/analytics/heatmap`,
      {
        start: timeRange.start.toISOString(),
        end: timeRange.end.toISOString(),
        workerIds,
      }
    );
    return response.data;
  }

  async getLocalizationScorecard(
    facilityId: string,
    timeRange: TimeRange,
    options?: {
      workerId?: string;
      limit?: number;
      jumpThreshold?: number;
    },
  ): Promise<LocalizationScorecard> {
    const response = await apiClient.get(
      `/facilities/${facilityId}/analytics/localization-scorecard`,
      {
        params: {
          startTime: timeRange.start.toISOString(),
          endTime: timeRange.end.toISOString(),
          workerId: options?.workerId,
          limit: options?.limit,
          jumpThreshold: options?.jumpThreshold,
        }
      }
    );
    return response.data.data || response.data;
  }

  async getActivityStatistics(
    facilityId: string,
    timeRange: TimeRange,
    workerIds?: string[]
  ) {
    const response = await apiClient.get(
      `/facilities/${facilityId}/analytics/activity-summary`,
      {
        params: {
          startTime: timeRange.start.toISOString(),
          endTime: timeRange.end.toISOString(),
          workerId: workerIds?.length === 1 ? workerIds[0] : undefined,
        }
      }
    );
    return response.data.data || response.data;
  }

  // Alerts endpoints
  async getAlerts(
    facilityId: string,
    params?: {
      severity?: string;
      status?: 'active' | 'acknowledged' | 'resolved';
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<Alert[]> {
    const response = await apiClient.get(
      `/facilities/${facilityId}/alerts`,
      { params }
    );
    return response.data.data || response.data;
  }

  // Worker-specific activities and alerts
  async getWorkerActivities(
    facilityId: string,
    workerId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      limit?: number;
    }
  ) {
    // Use activity timeline endpoint with workerId filter
    const endDate = params?.endDate || new Date().toISOString();
    const startDate = params?.startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    const response = await apiClient.get(`/facilities/${facilityId}/analytics/activity-timeline`, {
      params: {
        startTime: startDate,
        endTime: endDate,
        workerId,
        limit: params?.limit || 10
      }
    });
    return response.data.data || response.data || [];
  }

  async getWorkerAlerts(
    facilityId: string,
    _workerId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      limit?: number;
    }
  ): Promise<Alert[]> {
    // NOTE: backend getAlerts does not filter by workerId; omit the param to avoid confusion
    const response = await apiClient.get(`/facilities/${facilityId}/alerts`, {
      params: {
        startDate: params?.startDate,
        endDate: params?.endDate,
        limit: params?.limit || 10
      }
    });
    return response.data?.data || response.data || [];
  }

  async acknowledgeAlert(facilityId: string, alertId: string): Promise<Alert> {
    const response = await apiClient.post(
      `/facilities/${facilityId}/alerts/${alertId}/acknowledge`
    );
    return response.data;
  }

  async resolveAlert(facilityId: string, alertId: string): Promise<Alert> {
    const response = await apiClient.post(
      `/facilities/${facilityId}/alerts/${alertId}/resolve`
    );
    return response.data;
  }

  async getAlertStats(facilityId: string, params?: { days?: number }): Promise<any> {
    const response = await apiClient.get(
      `/facilities/${facilityId}/alerts/stats`,
      { params }
    );
    return response.data;
  }

  // Export endpoints
  async exportData(
    facilityId: string,
    params: {
      type: 'workers' | 'alerts' | 'analytics' | 'paths';
      format: 'csv' | 'json';
      startDate: string;
      endDate: string;
      workerIds?: string[];
    }
  ): Promise<Blob> {
    const response = await apiClient.post(
      `/facilities/${facilityId}/export`,
      params,
      { responseType: 'blob' }
    );
    return response.data;
  }

  // Configuration endpoints
  async getFacilityConfig(facilityId: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/config`);
    return response.data;
  }

  /** @deprecated Use getFacilityConfig() instead */
  async getSiteConfig(facilityId: string) {
    return this.getFacilityConfig(facilityId);
  }

  // Anchor management endpoints
  async getAnchors(facilityId: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/anchors`);
    return response.data.data || response.data;
  }

  /** @deprecated Use getAnchors() instead */
  async getBeacons(facilityId: string) {
    return this.getAnchors(facilityId);
  }

  async getAnchor(facilityId: string, anchorId: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/anchors/${anchorId}`);
    return response.data.data || response.data;
  }

  async autoPositionAnchors(facilityId: string) {
    const response = await apiClient.post(`/facilities/${facilityId}/anchors/auto-position`);
    return response.data;
  }

  /** @deprecated Use getAnchor() instead */
  async getBeacon(facilityId: string, anchorId: string) {
    return this.getAnchor(facilityId, anchorId);
  }

  async createAnchor(facilityId: string, anchorData: any) {
    const response = await apiClient.post(`/facilities/${facilityId}/anchors`, anchorData);
    return response.data;
  }

  /** @deprecated Use createAnchor() instead */
  async createBeacon(facilityId: string, anchorData: any) {
    return this.createAnchor(facilityId, anchorData);
  }

  async updateAnchor(facilityId: string, anchorId: string, updates: any) {
    const response = await apiClient.patch(
      `/facilities/${facilityId}/anchors/${anchorId}`,
      updates
    );
    return response.data;
  }

  /** @deprecated Use updateAnchor() instead */
  async updateBeacon(facilityId: string, anchorId: string, updates: any) {
    return this.updateAnchor(facilityId, anchorId, updates);
  }

  async deleteAnchor(facilityId: string, anchorId: string) {
    await apiClient.delete(`/facilities/${facilityId}/anchors/${anchorId}`);
  }

  /** @deprecated Use deleteAnchor() instead */
  async deleteBeacon(facilityId: string, anchorId: string) {
    return this.deleteAnchor(facilityId, anchorId);
  }

  // Zone management endpoints
  async getZones(facilityId: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/zones`);
    return response.data.data || response.data;
  }

  async getZone(facilityId: string, zoneId: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/zones/${zoneId}`);
    return response.data.data || response.data;
  }

  async createZone(facilityId: string, zoneData: any) {
    const response = await apiClient.post(`/facilities/${facilityId}/zones`, zoneData);
    return response.data;
  }

  async updateZone(facilityId: string, zoneId: string, updates: any) {
    const response = await apiClient.patch(
      `/facilities/${facilityId}/zones/${zoneId}`,
      updates
    );
    return response.data;
  }

  async deleteZone(facilityId: string, zoneId: string) {
    // The API is authoritative and rejects zones with linked anchors or workers.
    await apiClient.delete(`/facilities/${facilityId}/zones/${zoneId}`);
  }

  // Vehicle endpoints

  /**
   * Returns live vehicle positions for a facility.
   *
   * GET /api/v1/facilities/:facilityId/vehicles/positions
   *
   * Response: { success: true, data: [{ id, name, vehicleType,
   *   position: { x, y, heading, speedMps }, ... }] }
   */
  async getVehiclePositions(facilityId: string): Promise<any[]> {
    const response = await apiClient.get(
      `/facilities/${facilityId}/vehicles/positions`,
    );
    return response.data.data ?? response.data ?? [];
  }

  // Data query methods with time range support
  async queryWorkerLocations(
    facilityId: string,
    startTime: string,
    endTime: string,
    workerId?: string,
    maxAccuracyMeters?: number,
  ) {
    const response = await apiClient.get(`/facilities/${facilityId}/query/worker-locations`, {
      params: {
        startTime,
        endTime,
        ...(workerId ? { worker_id: workerId } : {}),
        ...(maxAccuracyMeters === undefined ? {} : { maxAccuracyMeters: String(maxAccuracyMeters) }),
      }
    });
    return response.data.data || response.data;
  }

  /**
   * Per-event classified activity via GET /facilities/:id/query/activities
   * (oldest first, at most 50,000 rows). Returns the rows plus the server's
   * cut: `truncated` is true when later events were not returned, and
   * `coveredUntil` is the last returned event's time.
   */
  async queryActivities(facilityId: string, startTime: string, endTime: string): Promise<{
    rows: any[];
    truncated: boolean;
    coveredUntil: string | null;
  }> {
    const response = await apiClient.get(`/facilities/${facilityId}/query/activities`, {
      params: { startTime, endTime }
    });
    const body = response.data ?? {};
    const rows = Array.isArray(body) ? body : Array.isArray(body.data) ? body.data : [];
    const meta = (!Array.isArray(body) && body.meta) || {};
    return {
      rows,
      truncated: meta.truncated === true,
      coveredUntil: typeof meta.coveredUntil === 'string' ? meta.coveredUntil : null,
    };
  }

  async submitActivityFeedback(feedback: any) {
    const response = await apiClient.post('/activities/feedback', feedback);
    return response.data;
  }

  async queryAlerts(facilityId: string, startTime: string, endTime: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/alerts`, {
      params: { startTime, endTime }
    });
    return response.data.data || response.data;
  }

  async queryAnchorReadings(facilityId: string, startTime: string, endTime: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/query/anchor-readings`, {
      params: { startTime, endTime }
    });
    return response.data.data || response.data;
  }

  /** @deprecated Use queryAnchorReadings() instead */
  async queryBeaconReadings(facilityId: string, startTime: string, endTime: string) {
    return this.queryAnchorReadings(facilityId, startTime, endTime);
  }

  async getActivityTimeline(facilityId: string, startTime: string, endTime: string, workerId?: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/analytics/activity-timeline`, {
      params: { startTime, endTime, workerId }
    });
    return response.data.data || response.data;
  }

  async getActivitySummary(facilityId: string, startTime: string, endTime: string, workerId?: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/analytics/activity-summary`, {
      params: { startTime, endTime, workerId }
    });
    return response.data.data || response.data;
  }

  async getMotionEventSummary(facilityId: string, startDate: string, endDate: string) {
    const response = await apiClient.get('/motion-events/summary', {
      params: { siteId: facilityId, startDate, endDate }
    });
    return response.data.data || response.data;
  }

  async getMotionWorkerTimeline(facilityId: string, workerId: string, date: string) {
    const response = await apiClient.get('/motion-events/worker-timeline', {
      params: { siteId: facilityId, workerId, date }
    });
    return response.data.data || response.data;
  }

  async getWorkerProductivity(facilityId: string, startTime: string, endTime: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/analytics/worker-productivity`, {
      params: { startTime, endTime }
    });
    return response.data.data || response.data;
  }

  async getZoneActivityAnalysis(facilityId: string, startTime: string, endTime: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/analytics/zone-activity`, {
      params: { startTime, endTime }
    });
    return response.data.data || response.data;
  }

  // Aggregated analytics endpoints (from analytics_daily_* tables)
  async getAggregatedFacilityAnalytics(facilityId: string, startDate: string, endDate: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/analytics/aggregated/site`, {
      params: { startDate, endDate }
    });
    return response.data;
  }

  /** @deprecated Use getAggregatedFacilityAnalytics() instead */
  async getAggregatedSiteAnalytics(facilityId: string, startDate: string, endDate: string) {
    return this.getAggregatedFacilityAnalytics(facilityId, startDate, endDate);
  }

  async getAggregatedWorkerAnalytics(facilityId: string, startDate: string, endDate: string, userId?: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/analytics/aggregated/workers`, {
      params: { startDate, endDate, userId }
    });
    return response.data;
  }

  async getAggregatedZoneAnalytics(facilityId: string, startDate: string, endDate: string, zoneId?: string) {
    const response = await apiClient.get(`/facilities/${facilityId}/analytics/aggregated/zones`, {
      params: { startDate, endDate, zoneId }
    });
    return response.data;
  }

  async getHighRiskWorkers(facilityId: string, params?: {
    riskType?: 'safety' | 'ergonomic' | 'fatigue';
    threshold?: number;
    days?: number;
  }) {
    const response = await apiClient.get(`/facilities/${facilityId}/analytics/high-risk-workers`, {
      params
    });
    return response.data;
  }
}

// Create and export singleton instance
export const api = new ApiService();

// Export for use in Redux slices
export default api;
